import { useEffect, useRef } from "react";
import { Download, Printer, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface PdfPreviewModalProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  pdfUrl: string | null;
  filename: string;
  /** Title shown in the dialog header (defaults to "Document PDF"). */
  title?: string;
  /**
   * Reference number rendered after the title, e.g. the quotation or waybill
   * number. Also accepts the legacy `quoteNumber` alias from the original
   * quotation-only call sites.
   */
  documentNumber?: string;
  quoteNumber?: string;
}

// Shared PDF preview modal used by both the Quotation and Waybill flows.
// Renders the supplied object URL in an iframe with Print / Download / Close
// actions. The caller owns the object URL lifecycle — revoke it after the
// modal closes to avoid leaking memory.
export default function PdfPreviewModal({
  open,
  onOpenChange,
  pdfUrl,
  filename,
  title = "Document PDF",
  documentNumber,
  quoteNumber,
}: PdfPreviewModalProps) {
  const refNumber = documentNumber || quoteNumber;
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Best-effort focus the iframe once the dialog has settled so the embedded
  // PDF viewer responds to Ctrl/Cmd+P shortcuts immediately.
  useEffect(() => {
    if (!open || !pdfUrl) return;
    const t = window.setTimeout(() => iframeRef.current?.focus(), 200);
    return () => window.clearTimeout(t);
  }, [open, pdfUrl]);

  function handleDownload() {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function handlePrint() {
    const frame = iframeRef.current;
    if (!frame || !frame.contentWindow) {
      if (pdfUrl) window.open(pdfUrl, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    } catch {
      if (pdfUrl) window.open(pdfUrl, "_blank", "noopener,noreferrer");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[95vw] h-[90vh] p-0 gap-0 flex flex-col">
        <DialogHeader className="flex flex-row items-center justify-between gap-3 px-4 py-3 border-b space-y-0">
          <DialogTitle className="text-base font-semibold">
            {title}
            {refNumber ? ` — ${refNumber}` : ""}
          </DialogTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-8 px-3"
              onClick={handlePrint}
              disabled={!pdfUrl}
            >
              <Printer className="h-4 w-4 mr-1" />
              Print
            </Button>
            <Button
              size="sm"
              className="bg-violet-500 hover:bg-violet-400 text-white h-8 px-3"
              onClick={handleDownload}
              disabled={!pdfUrl}
            >
              <Download className="h-4 w-4 mr-1" />
              Download
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 px-2"
              onClick={() => onOpenChange(false)}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>
        <div className="flex-1 bg-white">
          {pdfUrl ? (
            <iframe
              ref={iframeRef}
              src={pdfUrl}
              title={filename}
              className="w-full h-full"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-sm text-slate-500">
              Preparing PDF…
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
